import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Table,
  Space,
  Tooltip,
  Card,
  notification,
  TimePicker,
  InputNumber,
  ConfigProvider,
} from 'antd';
import React, { Suspense, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import userHaveRolePermission from '../../../../helpers/userHaveRolePermission';
import { PAGE_URLS } from '../../../../helpers/endpoints';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import {
  addSlot,
  deleteSlot,
  editSlot,
  getDownloadLinkSlotCapacity,
  getSlotsByZoneIdForTwoDays,
  getZonesByStoreIdForSlots,
} from 'store/requests/slot';
import { getStoreList } from 'store/requests/definitions';
import moment from 'moment';
import ImportBulkCapacityUpdate from 'components/ImportExcel/ImportBulkCapacityUpdate';
import { SLOT_MONITORING_DAYS } from 'helpers/constants';
import trTR from 'antd/es/locale/tr_TR';
import { FILE_TEMPLATE_CODE } from 'helpers/constants';

const { Option } = Select;

const SlotMonitoring = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [formToday] = Form.useForm();
  const [formTomorrow] = Form.useForm();

  const [editingKeyToday, setEditingKeyToday] = useState(null);
  const [editingKeyTomorrow, setEditingKeyTomorrow] = useState(null);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectDate, setSelectDate] = useState(null);
  const [storeInputVisible, setStoreInputVisible] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [slotsForTwoDay, setSlotsForTwoDay] = useState([]);
  const [slotsForToday, setSlotsForToday] = useState([]);
  const [todayOccupancy, setTodayOccupancy] = useState(0);
  const [tomorrowOccupancy, setTomorrowOccupancy] = useState(0);
  const [slotsForTomorrow, setSlotsForTomorrow] = useState([]);
  const [isLoadingZones, setIsLoadingZones] = useState(false);
  const [selectedZoneId, setSelectedZoneId] = useState(zones[0]?.id);
  const [formattedTodayDate, setFormattedTodayDate] = useState(null);
  const [formattedTomorrowDate, setFormattedTomorrowDate] = useState(null);
  const [editedSlots, setEditedSlots] = useState([]);
  const [deletedSlots, setDeletedSlots] = useState([]);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [slotCapacityLink, setSlotCapacityLink] = useState(null);
  const [addSlotButtonDisabled, setAddSlotButtonDisabled] = useState(false);

  const activeRegion = useSelector((state) => state.auth?.user?.activeRegion);
  const activeStore = useSelector((state) => state.auth?.user?.activeStore);

  const { store, zone, zonesOfStore } =
    useSelector((state) => state?.zone?.slotsFromZone) ?? null;

  useEffect(() => {
    (async () => {
      const today = moment.utc().startOf('day').valueOf();
      setSelectDate(today);
      const formattedSelectedDate = moment(today).format('DD/MM/YYYY');
      const nextDay = moment(today).add(1, 'days').startOf('day');
      const formattedNextDay = nextDay.format('DD/MM/YYYY');
      setFormattedTodayDate(formattedSelectedDate);
      setFormattedTomorrowDate(formattedNextDay);

      setSlotCapacityLink(FILE_TEMPLATE_CODE.SLOT_CAPACITY_UPDATE);
    })();
  }, []);

  useEffect(() => {
    const averageOccupancy = calculateOccupancy(slotsForToday);
    const formattedAverageOccupancy = (
      Math.round(averageOccupancy * 100) / 100
    ).toFixed(2);

    setTodayOccupancy(formattedAverageOccupancy);
  }, [slotsForToday]);

  useEffect(() => {
    const averageOccupancy = calculateOccupancy(slotsForTomorrow);
    const formattedAverageOccupancy = (
      Math.round(averageOccupancy * 100) / 100
    ).toFixed(2);

    setTomorrowOccupancy(formattedAverageOccupancy);
  }, [slotsForTomorrow]);

  useEffect(() => {}, [activeRegion]);

  useEffect(() => {
    if (zonesOfStore.length > 0) {
      setStoreInputVisible(true);
      setZones(zonesOfStore);
      setSelectedZoneId(zone?.id);
      setStores([store]);
      setSelectedStore(store?.id);
    } else {
      if (activeStore) {
        setIsLoadingZones(true);
        getZonesByStoreIdForSlots(
          `filter=store.id==${activeStore?.id}&size=2000`
        )
          .then((zones) => {
            const { content } = zones ?? [];
            setZones(content ? content : []);
            setSelectedZoneId(content[0]?.id);
          })
          .finally(() => {
            setIsLoadingZones(false);
          });
      } else if (activeRegion) {
        setStoreInputVisible(false);

        getStoreList(`filter=region.id==${activeRegion.id}&size=2000`).then(
          (storeList) => {
            setStores(storeList.content);
          }
        );
        setStoreInputVisible(true);
      } else {
        setStoreInputVisible(false);
      }
    }
  }, [activeStore, activeRegion, zone]);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (selectedZoneId && selectDate) {
        const slots = await getSlotsByZoneIdForTwoDays(
          selectedZoneId,
          selectDate
        );
        if (slots && slots.length > 1) {
          slots.sort((a, b) => a.date - b.date);
        }
        if (slots.length > 0) {
          setSlotsForTwoDay(slots);
        } else {
          setSlotsForTwoDay([
            { date: selectDate, slots: [] },
            {
              date: moment
                .utc(selectDate)
                .add(1, 'days')
                .startOf('day')
                .valueOf(),
              slots: [],
            },
          ]);
        }

        const today = moment.utc(selectDate).startOf('day');
        const tomorrow = moment.utc(selectDate).add(1, 'days').startOf('day');

        const todaySlots =
          slots
            .find((day) => moment.utc(day.date).isSame(today, 'day'))
            ?.slots?.sort((a, b) => a.slotTime.localeCompare(b.slotTime)) || [];
        const tomorrowSlots =
          slots
            .find((day) => moment.utc(day.date).isSame(tomorrow, 'day'))
            ?.slots?.sort((a, b) => a.slotTime.localeCompare(b.slotTime)) || [];

        setSlotsForToday(todaySlots);
        setSlotsForTomorrow(tomorrowSlots);
      }
    } catch (error) {
      console.error('Error', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedZoneId && selectDate) {
      const formattedSelectedDate = moment(selectDate).format('DD/MM/YYYY');
      const nextDay = moment(selectDate).add(1, 'days');
      const formattedNextDay = nextDay.format('DD/MM/YYYY');
      setFormattedTodayDate(formattedSelectedDate);
      setFormattedTomorrowDate(formattedNextDay);
      fetchData();
    }
  }, [selectDate, selectedZoneId]);

  const handleChangeStore = (value) => {
    setLoading(true);
    setZones([]);
    setSelectedStore(value);
    getZonesByStoreIdForSlots(`filter=store.id==${value}&size=2000`)
      .then((zones) => {
        const { content } = zones ?? [];
        setZones(content ? content : []);
        if (content) {
          setSelectedZoneId(content[0]?.id);
        }
        setIsLoadingZones(false);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const isEditDisabled = (endTime, slotDate) => {
    if (!slotDate) {
      return true;
    }

    const currentTime = moment();
    const formattedEndTime = moment(endTime, 'HH:mm');
    const formattedDate = moment(slotDate, 'DD/MM/YYYY');

    return (
      formattedDate.isBefore(currentTime, 'day') ||
      (formattedDate.isSame(currentTime, 'day') &&
        formattedEndTime.isBefore(currentTime))
    );
  };

  const handleChangeZone = (value) => {
    setIsLoadingZones(true);
    setSelectedZoneId(value);
    setIsLoadingZones(false);
  };

  const notificationEdit = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('slotsEdited'),
    });
  };

  const saveCurrentSlots = async () => {
    setLoading(true);
    const filteredEditedSlots = editedSlots.filter((editedSlot) => {
      return !deletedSlots.some(
        (deletedSlot) => deletedSlot.id === editedSlot.id
      );
    });

    try {
      if (deletedSlots.length === 0 && filteredEditedSlots.length === 0) {
        notification.warn({
          message: t('changedSlotNotFound'),
        });
        return;
      }
      const today = moment.utc(selectDate).startOf('day').valueOf();
      const tomorrow = moment
        .utc(selectDate)
        .add(1, 'days')
        .startOf('day')
        .valueOf();

      for (const slot of deletedSlots) {
        if (slot.id && !slot.isNewAdded) {
          await deleteSlot(slot.id);
        }
      }
      for (const slot of filteredEditedSlots) {
        const id = slot?.id;
        delete slot?.id;
        const isTodaySlot =
          slotsForToday.some((s) => s.id === id) ||
          (slot.isNewAdded && slotsForToday.some((s) => s.id === slot.id)) ||
          (slot.isNewAdded && editingKeyToday === slot.id);
        slot.date = isTodaySlot ? today : tomorrow;

        if (slot.isNewAdded) {
          const payload = {
            zoneId: selectedZoneId,
            date: slot.date,
            startTime: slot.startTime,
            endTime: slot.endTime,
            cutOffTime: slot.cutOffTime,
            capacity: slot.capacity,
            slotDeliveryFeeList: slot.slotDeliveryFees,
          };
          await addSlot(payload);
        } else if (id) {
          const editedSlot = {
            ...slot,
            slotDeliveryFeeList: slot.slotDeliveryFees,
          };
          delete editedSlot.slotDeliveryFees;
          await editSlot(editedSlot, id);
        }
      }
      notificationEdit('success');
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    } finally {
      setDeletedSlots([]);
      setEditedSlots([]);
      setLoading(false);
    }
    setLoading(true);
    fetchData();
    setLoading(false);
  };

  const columnsToday = [
    {
      title: t('slots'),
      dataIndex: 'slotTime',
      width: '25%',
      editable: true,
      render: (text, record) => {
        const isEditing = record.id === editingKeyToday;

        return isEditing ? (
          <Form.Item
            name="slotTime"
            initialValue={text}
            rules={[
              {
                required: true,
                message: t('enterValue'),
              },
            ]}
            style={{ width: 165 }}
          >
            <TimePicker.RangePicker
              minuteStep={15}
              showSecond={false}
              format="HH:mm"
              size="medium"
              placeholder={[
                t('slotManagement.startTime'),
                t('slotManagement.finishTime'),
              ]}
            />
          </Form.Item>
        ) : (
          text
        );
      },
    },
    {
      title: 'CutOff',
      dataIndex: 'cutOffTime',
      editable: true,
      width: '15%',
      render: (text, record) => {
        const isEditing = record.id === editingKeyToday;

        return isEditing ? (
          <Form.Item
            name="cutOffTime"
            initialValue={text}
            rules={[
              {
                required: true,
                message: t('enterValue'),
              },
            ]}
            style={{ width: 90 }}
          >
            <TimePicker format="HH:mm" placeholder="CutOff" />
          </Form.Item>
        ) : (
          text
        );
      },
    },
    {
      title: t('slotManagement.slotsCapacity'),
      dataIndex: 'capacity',
      editable: true,
      width: '10%',
      render: (text, record) => {
        const isEditing = record.id === editingKeyToday;
        return isEditing ? (
          <Form.Item
            name="capacity"
            initialValue={text}
            rules={[
              {
                required: true,
                message: t('enterCapacity'),
              },
              {
                validator: async (_, value) => {
                  if (value < record.currentOrderCount) {
                    return Promise.reject(
                      t('capacityShouldBeGreaterThanCurrentOrderCount')
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              placeholder={t('slotManagement.slotsCapacity')}
              min={0}
              maxLength={11}
              size="medium"
              style={{ width: 100 }}
            />
          </Form.Item>
        ) : (
          text
        );
      },
    },
    {
      title: t('deliveryFeeDtoList'),
      dataIndex: 'slotDeliveryFees',
      editable: true,
      width: '40%',
      render: (text, record) => {
        const isEditing = record.id === editingKeyToday;
        return isEditing ? (
          <Form.List
            name="slotDeliveryFees"
            initialValue={
              record.slotDeliveryFees || [
                {
                  minCartAmount: 0.0,
                  maxCartAmount: null,
                  deliveryFee: null,
                },
              ]
            }
          >
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Row
                    key={field.key}
                    gutter={8}
                    style={{ marginBottom: '8px' }}
                  >
                    <Col span={24}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: '8px',
                        }}
                      >
                        <Form.Item
                          label={t('minimumCartAmount')}
                          {...field}
                          name={[field.name, 'minCartAmount']}
                          fieldKey={[field.fieldKey, 'minCartAmount']}
                          rules={[
                            {
                              required: true,
                              message: t('alert.fillField'),
                            },
                          ]}
                          style={{ margin: 0, width: '33%' }}
                        >
                          <InputNumber
                            min={0}
                            size="small"
                            step={0.1}
                            maxLength={11}
                            style={{ width: '100%' }}
                            disabled={field.name > 0}
                            formatter={(value) =>
                              value
                                ? value.replace(/(\.\d{2})\d+/, '$1')
                                : value
                            }
                            parser={(value) => value.replace(/[^\d.]/g, '')}
                          />
                        </Form.Item>
                        <Form.Item
                          label={t('maximumCartAmount')}
                          {...field}
                          name={[field.name, 'maxCartAmount']}
                          fieldKey={[field.fieldKey, 'maxCartAmount']}
                          rules={[
                            {
                              required: true,
                              message: t('alert.fillField'),
                            },
                          ]}
                          style={{ margin: 0, width: '33%' }}
                        >
                          <InputNumber
                            min={0}
                            size="small"
                            step={0.1}
                            maxLength={11}
                            style={{ width: '100%' }}
                            formatter={(value) =>
                              value
                                ? value.replace(/(\.\d{2})\d+/, '$1')
                                : value
                            }
                            parser={(value) => value.replace(/[^\d.]/g, '')}
                          />
                        </Form.Item>
                        <Form.Item
                          label={t('deliveryFee')}
                          {...field}
                          name={[field.name, 'deliveryFee']}
                          fieldKey={[field.fieldKey, 'deliveryFee']}
                          rules={[
                            {
                              required: true,
                              message: t('alert.fillField'),
                            },
                          ]}
                          style={{ margin: 0, width: '33%' }}
                        >
                          <InputNumber
                            min={0}
                            size="small"
                            step={0.1}
                            maxLength={11}
                            style={{ width: '100%' }}
                            formatter={(value) =>
                              value
                                ? value.replace(/(\.\d{2})\d+/, '$1')
                                : value
                            }
                            parser={(value) => value.replace(/[^\d.]/g, '')}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                ))}
                <Row>
                  <Col span={24}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '8px',
                      }}
                    >
                      {fields.length > 0 && (
                        <MinusCircleOutlined
                          style={{ fontSize: '16px', cursor: 'pointer' }}
                          onClick={() => remove(fields[fields.length - 1].name)}
                        />
                      )}
                      <PlusCircleOutlined
                        style={{ fontSize: '16px', cursor: 'pointer' }}
                        onClick={() => {
                          if (fields.length > 0) {
                            const lastField = fields[fields.length - 1];
                            formToday
                              .validateFields([
                                [
                                  'slotDeliveryFees',
                                  lastField.name,
                                  'minCartAmount',
                                ],
                                [
                                  'slotDeliveryFees',
                                  lastField.name,
                                  'maxCartAmount',
                                ],
                                [
                                  'slotDeliveryFees',
                                  lastField.name,
                                  'deliveryFee',
                                ],
                              ])
                              .then(() => {
                                const lastItem = formToday.getFieldValue([
                                  'slotDeliveryFees',
                                  lastField.name,
                                ]);
                                add({
                                  minCartAmount: lastItem
                                    ? lastItem.maxCartAmount + 0.01
                                    : 0.0,
                                  maxCartAmount: null,
                                  deliveryFee: null,
                                });
                              })
                              .catch((errorInfo) => {
                                console.log('Validation failed:', errorInfo);
                              });
                          } else {
                            add({
                              minCartAmount: 0.0,
                              maxCartAmount: null,
                              deliveryFee: null,
                            });
                          }
                        }}
                      />
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </Form.List>
        ) : record.slotDeliveryFees && record.slotDeliveryFees.length > 0 ? (
          <div>
            {record.slotDeliveryFees.map((fee) => (
              <span key={`${fee.minCartAmount}-${fee.maxCartAmount}`}>
                {fee.minCartAmount}-{fee.maxCartAmount}:{' '}
                <span style={{ fontWeight: 500 }}>{fee.deliveryFee}</span>,
                <br />
              </span>
            ))}
          </div>
        ) : (
          <div>-</div>
        );
      },
    },
    {
      title: t('orderCount'),
      dataIndex: 'currentOrderCount',
      width: '%5',
      editable: false,
    },
    {
      title: t('fullnessRatio'),
      dataIndex: 'occupancy',
      width: '%5',
      editable: false,
    },
    {
      title: t('operations'),
      key: 'action',
      width: '%25',
      render: (text, record) => {
        const isEditing = record.id === editingKeyToday;
        return (
          <Space size="small">
            {isEditing ? (
              <Button
                type="primary"
                size="small"
                onClick={() =>
                  handleSaveSlot(record.id, SLOT_MONITORING_DAYS.TODAY)
                }
              >
                {t('save')}
              </Button>
            ) : (
              <Tooltip title={t('edit')}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  disabled={isEditDisabled(record.endTime, formattedTodayDate)}
                  onClick={() =>
                    handleEditSlot(record.id, SLOT_MONITORING_DAYS.TODAY)
                  }
                />
              </Tooltip>
            )}
            <Popconfirm
              title={t('confirmDeleteRow')}
              onConfirm={() =>
                handleDeleteSlot(record.id, SLOT_MONITORING_DAYS.TODAY)
              }
              okText={t('yes')}
              cancelText={t('no')}
              disabled={
                record.currentOrderCount > 0 ||
                isEditDisabled(record.endTime, formattedTodayDate)
              }
            >
              <Tooltip title={t('slotManagement.delete')}>
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={
                    record.currentOrderCount > 0 ||
                    isEditDisabled(record.endTime, formattedTodayDate)
                  }
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];
  const columnsTomorrow = [
    {
      title: t('slots'),
      dataIndex: 'slotTime',
      width: '25%',
      editable: true,
      render: (text, record) => {
        const isEditing = record.id === editingKeyTomorrow;

        return isEditing ? (
          <Form.Item
            name="slotTime"
            initialValue={text}
            rules={[
              {
                required: true,
                message: t('enterValue'),
              },
            ]}
            style={{ width: 165 }}
          >
            <TimePicker.RangePicker
              minuteStep={15}
              showSecond={false}
              format="HH:mm"
              size="medium"
              placeholder={[
                t('slotManagement.startTime'),
                t('slotManagement.finishTime'),
              ]}
            />
          </Form.Item>
        ) : (
          text
        );
      },
    },
    {
      title: 'CutOff',
      dataIndex: 'cutOffTime',
      editable: true,
      width: '15%',
      render: (text, record) => {
        const isEditing = record.id === editingKeyTomorrow;

        return isEditing ? (
          <Form.Item
            name="cutOffTime"
            initialValue={text}
            rules={[
              {
                required: true,
                message: t('enterValue'),
              },
            ]}
            style={{ width: 90 }}
          >
            <TimePicker format="HH:mm" placeholder="CutOff" />
          </Form.Item>
        ) : (
          text
        );
      },
    },
    {
      title: t('slotManagement.slotsCapacity'),
      dataIndex: 'capacity',
      editable: true,
      width: '10%',
      render: (text, record) => {
        const isEditing = record.id === editingKeyTomorrow;
        return isEditing ? (
          <Form.Item
            name="capacity"
            initialValue={text}
            rules={[
              {
                required: true,
                message: t('enterCapacity'),
              },
              {
                validator: async (_, value) => {
                  if (value < record.currentOrderCount) {
                    return Promise.reject(
                      t('capacityShouldBeGreaterThanCurrentOrderCount')
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              placeholder={t('slotManagement.slotsCapacity')}
              min={0}
              maxLength={11}
              size="medium"
              style={{ width: 100 }}
            />
          </Form.Item>
        ) : (
          text
        );
      },
    },
    {
      title: t('deliveryFeeDtoList'),
      dataIndex: 'slotDeliveryFees',
      editable: true,
      width: '40%',
      render: (text, record) => {
        const isEditing = record.id === editingKeyTomorrow;
        return isEditing ? (
          <Form.List
            name="slotDeliveryFees"
            initialValue={
              record.slotDeliveryFees || [
                {
                  minCartAmount: 0.0,
                  maxCartAmount: null,
                  deliveryFee: null,
                },
              ]
            }
          >
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Row
                    key={field.key}
                    gutter={8}
                    style={{ marginBottom: '8px' }}
                  >
                    <Col span={24}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: '8px',
                        }}
                      >
                        <Form.Item
                          label={t('minimumCartAmount')}
                          {...field}
                          name={[field.name, 'minCartAmount']}
                          fieldKey={[field.fieldKey, 'minCartAmount']}
                          rules={[
                            {
                              required: true,
                              message: t('alert.fillField'),
                            },
                          ]}
                          style={{ margin: 0, width: '33%' }}
                        >
                          <InputNumber
                            min={0}
                            size="small"
                            step={0.1}
                            maxLength={11}
                            style={{ width: '100%' }}
                            disabled={field.name > 0}
                            formatter={(value) =>
                              value
                                ? value.replace(/(\.\d{2})\d+/, '$1')
                                : value
                            }
                            parser={(value) => value.replace(/[^\d.]/g, '')}
                          />
                        </Form.Item>
                        <Form.Item
                          label={t('maximumCartAmount')}
                          {...field}
                          name={[field.name, 'maxCartAmount']}
                          fieldKey={[field.fieldKey, 'maxCartAmount']}
                          rules={[
                            {
                              required: true,
                              message: t('alert.fillField'),
                            },
                          ]}
                          style={{ margin: 0, width: '33%' }}
                        >
                          <InputNumber
                            min={0}
                            size="small"
                            step={0.1}
                            maxLength={11}
                            style={{ width: '100%' }}
                            formatter={(value) =>
                              value
                                ? value.replace(/(\.\d{2})\d+/, '$1')
                                : value
                            }
                            parser={(value) => value.replace(/[^\d.]/g, '')}
                          />
                        </Form.Item>
                        <Form.Item
                          label={t('deliveryFee')}
                          {...field}
                          name={[field.name, 'deliveryFee']}
                          fieldKey={[field.fieldKey, 'deliveryFee']}
                          rules={[
                            {
                              required: true,
                              message: t('alert.fillField'),
                            },
                          ]}
                          style={{ margin: 0, width: '33%' }}
                        >
                          <InputNumber
                            min={0}
                            size="small"
                            step={0.1}
                            maxLength={11}
                            style={{ width: '100%' }}
                            formatter={(value) =>
                              value
                                ? value.replace(/(\.\d{2})\d+/, '$1')
                                : value
                            }
                            parser={(value) => value.replace(/[^\d.]/g, '')}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                ))}
                <Row>
                  <Col span={24}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '8px',
                      }}
                    >
                      {fields.length > 0 && (
                        <MinusCircleOutlined
                          style={{ fontSize: '16px', cursor: 'pointer' }}
                          onClick={() => remove(fields[fields.length - 1].name)}
                        />
                      )}
                      <PlusCircleOutlined
                        style={{ fontSize: '16px', cursor: 'pointer' }}
                        onClick={() => {
                          if (fields.length > 0) {
                            const lastField = fields[fields.length - 1];
                            formTomorrow
                              .validateFields([
                                [
                                  'slotDeliveryFees',
                                  lastField.name,
                                  'minCartAmount',
                                ],
                                [
                                  'slotDeliveryFees',
                                  lastField.name,
                                  'maxCartAmount',
                                ],
                                [
                                  'slotDeliveryFees',
                                  lastField.name,
                                  'deliveryFee',
                                ],
                              ])
                              .then(() => {
                                const lastItem = formTomorrow.getFieldValue([
                                  'slotDeliveryFees',
                                  lastField.name,
                                ]);
                                add({
                                  minCartAmount: lastItem
                                    ? lastItem.maxCartAmount + 0.01
                                    : 0.0,
                                  maxCartAmount: null,
                                  deliveryFee: null,
                                });
                              })
                              .catch((errorInfo) => {
                                console.log('Validation failed:', errorInfo);
                              });
                          } else {
                            add({
                              minCartAmount: 0.0,
                              maxCartAmount: null,
                              deliveryFee: null,
                            });
                          }
                        }}
                      />
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </Form.List>
        ) : record.slotDeliveryFees && record.slotDeliveryFees.length > 0 ? (
          <div>
            {record.slotDeliveryFees.map((fee) => (
              <span key={`${fee.minCartAmount}-${fee.maxCartAmount}`}>
                {fee.minCartAmount}-{fee.maxCartAmount}:{' '}
                <span style={{ fontWeight: 500 }}>{fee.deliveryFee}</span>,
                <br />
              </span>
            ))}
          </div>
        ) : (
          <div>-</div>
        );
      },
    },
    {
      title: t('orderCount'),
      dataIndex: 'currentOrderCount',
      width: '%5',
      editable: false,
    },
    {
      title: t('fullnessRatio'),
      dataIndex: 'occupancy',
      width: '%5',
      editable: false,
    },
    {
      title: t('operations'),
      key: 'action',
      width: '%25',
      render: (text, record) => {
        const isEditing = record.id === editingKeyTomorrow;
        return (
          <Space size="small">
            {isEditing ? (
              <Button
                type="primary"
                size="small"
                onClick={() =>
                  handleSaveSlot(record.id, SLOT_MONITORING_DAYS.TOMORROW)
                }
              >
                {t('save')}
              </Button>
            ) : (
              <Tooltip title={t('edit')}>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  disabled={isEditDisabled(
                    record.endTime,
                    formattedTomorrowDate
                  )}
                  onClick={() =>
                    handleEditSlot(record.id, SLOT_MONITORING_DAYS.TOMORROW)
                  }
                />
              </Tooltip>
            )}
            <Popconfirm
              title={t('confirmDeleteRow')}
              onConfirm={() =>
                handleDeleteSlot(record.id, SLOT_MONITORING_DAYS.TOMORROW)
              }
              okText={t('yes')}
              cancelText={t('no')}
              disabled={
                record.currentOrderCount > 0 ||
                isEditDisabled(record.endTime, formattedTomorrowDate)
              }
            >
              <Tooltip title={t('slotManagement.delete')}>
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={
                    record.currentOrderCount > 0 ||
                    isEditDisabled(record.endTime, formattedTomorrowDate)
                  }
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];
  const calculateOccupancy = (slots) => {
    if (!slots || slots.length === 0) {
      return 0;
    }

    const totalCapacity = slots.reduce(
      (total, slot) => total + +slot.capacity || 0,
      0
    );
    const totalOrderCount = slots.reduce(
      (total, slot) => total + +slot.currentOrderCount || 0,
      0
    );

    const occupancy = (totalOrderCount / totalCapacity) * 100;
    return isNaN(occupancy) ? 0 : occupancy;
  };

  const handleEditSlot = (key, editedDay) => {
    editedDay === SLOT_MONITORING_DAYS.TODAY
      ? setEditingKeyToday(key)
      : setEditingKeyTomorrow(key);

    const editedSlot =
      editedDay === SLOT_MONITORING_DAYS.TODAY
        ? slotsForToday.find((item) => item.id === key)
        : slotsForTomorrow.find((item) => item.id === key);

    const editedForm =
      editedDay === SLOT_MONITORING_DAYS.TODAY ? formToday : formTomorrow;

    editedForm.setFieldsValue({
      capacity: editedSlot?.capacity || '',
      cutOffTime: moment(editedSlot.cutOffTime, 'HH:mm') || '08:00',
      slotTime:
        [
          moment(editedSlot.slotTime.split('-')[0], 'HH:mm'),
          moment(editedSlot.slotTime.split('-')[1], 'HH:mm'),
        ] || '',
      slotDeliveryFees: editedSlot?.slotDeliveryFees || [
        {
          minCartAmount: 0.0,
          maxCartAmount: null,
          deliveryFee: null,
        },
      ],
    });

    const today = moment.utc(selectDate).startOf('day').valueOf();
    const tomorrow = moment
      .utc(selectDate)
      .add(1, 'days')
      .startOf('day')
      .valueOf();
    editedSlot.date =
      editedDay === SLOT_MONITORING_DAYS.TODAY ? today : tomorrow;

    const updatedEditedSlots = editedSlots.filter((item) => item.id !== key);
    const {
      code,
      available,
      occupancy,
      currentOrderCount,
      slotTime,
      ...strippedSlot
    } = editedSlot;
    setEditedSlots([...updatedEditedSlots, strippedSlot]);
  };

  const handleSaveSlot = async (key, day) => {
    try {
      if (day === SLOT_MONITORING_DAYS.TODAY) {
        const row = await formToday.validateFields();
        const newData = [...slotsForToday];

        rowSave(key, row, newData, SLOT_MONITORING_DAYS.TODAY);
      } else {
        const row = await formTomorrow.validateFields();
        const newData = [...slotsForTomorrow];

        rowSave(key, row, newData, SLOT_MONITORING_DAYS.TOMORROW);
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const rowSave = (key, row, newData, day) => {
    const index = newData.findIndex((item) => key === item.id);

    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, { ...item, ...row });

      const occupancy =
        +row.capacity !== 0 && +item.currentOrderCount !== 0
          ? ((item.currentOrderCount / row.capacity) * 100).toFixed(0)
          : 0;
      newData[index].occupancy = `${occupancy}%`;
      newData[index].cutOffTime = moment(row.cutOffTime, 'HH:mm').format(
        'HH:mm'
      );
      newData[index].slotTime = `${moment(row.slotTime[0], 'HH:mm').format(
        'HH:mm'
      )}-${moment(row.slotTime[1], 'HH:mm').format('HH:mm')}`;
      newData[index].startTime = moment(row.slotTime[0], 'HH:mm').format(
        'HH:mm'
      );
      newData[index].endTime = moment(row.slotTime[1], 'HH:mm').format('HH:mm');
      newData[index].slotDeliveryFees = row.slotDeliveryFees;

      const today = moment.utc(selectDate).startOf('day').valueOf();
      const tomorrow = moment
        .utc(selectDate)
        .add(1, 'days')
        .startOf('day')
        .valueOf();
      newData[index].date =
        day === SLOT_MONITORING_DAYS.TODAY ? today : tomorrow;

      const editedSlotIndex = editedSlots.findIndex((item) => item.id === key);
      if (editedSlotIndex > -1) {
        editedSlots[editedSlotIndex].capacity = row.capacity;
        editedSlots[editedSlotIndex].cutOffTime = moment(row.cutOffTime).format(
          'HH:mm'
        );
        editedSlots[editedSlotIndex].slotTime = `${moment(
          row.slotTime[0],
          'HH:mm'
        ).format('HH:mm')}-${moment(row.slotTime[1], 'HH:mm').format('HH:mm')}`;
        editedSlots[editedSlotIndex].startTime = moment(
          row.slotTime[0],
          'HH:mm'
        ).format('HH:mm');
        editedSlots[editedSlotIndex].endTime = moment(
          row.slotTime[1],
          'HH:mm'
        ).format('HH:mm');
        editedSlots[editedSlotIndex].slotDeliveryFees = row.slotDeliveryFees;
        editedSlots[editedSlotIndex].date =
          day === SLOT_MONITORING_DAYS.TODAY ? today : tomorrow;
      }
    }

    const setSlotsFunction =
      day === SLOT_MONITORING_DAYS.TODAY
        ? setSlotsForToday
        : setSlotsForTomorrow;
    const formFunction =
      day === SLOT_MONITORING_DAYS.TODAY ? formToday : formTomorrow;
    const setEditingKeyFunction =
      day === SLOT_MONITORING_DAYS.TODAY
        ? setEditingKeyToday
        : setEditingKeyTomorrow;

    setSlotsFunction(newData);
    formFunction.setFieldsValue({
      cutOffTime: undefined,
      slotTime: undefined,
      capacity: undefined,
      slotDeliveryFees: undefined,
    });

    setEditingKeyFunction('');
    setAddSlotButtonDisabled(false);
  };

  const handleDeleteSlot = (key, day) => {
    const deletedSlot =
      day === SLOT_MONITORING_DAYS.TODAY
        ? slotsForToday.find((item) => item.id === key)
        : slotsForTomorrow.find((item) => item.id === key);
    if (deletedSlots.some((slot) => slot.id === key)) {
      return;
    }
    const today = moment.utc(selectDate).startOf('day').valueOf();
    const tomorrow = moment
      .utc(selectDate)
      .add(1, 'days')
      .startOf('day')
      .valueOf();
    deletedSlot.date = day === SLOT_MONITORING_DAYS.TODAY ? today : tomorrow;
    setDeletedSlots([...deletedSlots, deletedSlot]);

    if (day === SLOT_MONITORING_DAYS.TODAY) {
      const newData = [...slotsForToday];
      setSlotsForToday(newData.filter((item) => item.id !== key));
    } else {
      const newData = [...slotsForTomorrow];
      setSlotsForTomorrow(newData.filter((item) => item.id !== key));
    }
    setAddSlotButtonDisabled(false);
  };

  const handleAddRow = (day) => {
    const today = moment.utc(selectDate).startOf('day').valueOf();
    const tomorrow = moment
      .utc(selectDate)
      .add(1, 'days')
      .startOf('day')
      .valueOf();

    const newEmptyRow = {
      slotTime: '',
      cutOffTime: '',
      capacity: '',
      slotDeliveryFees: [
        {
          minCartAmount: 0.0,
          maxCartAmount: null,
          deliveryFee: null,
        },
      ],
      currentOrderCount: 0,
      occupancy: '0%',
      isNewAdded: true,
      id: Math.random().toString(36).substring(2) + Date.now().toString(36),
      date: day === SLOT_MONITORING_DAYS.TODAY ? today : tomorrow,
    };

    day === SLOT_MONITORING_DAYS.TODAY
      ? setSlotsForToday([newEmptyRow, ...slotsForToday])
      : setSlotsForTomorrow([newEmptyRow, ...slotsForTomorrow]);
    day === SLOT_MONITORING_DAYS.TODAY
      ? setEditingKeyToday(newEmptyRow.id)
      : setEditingKeyTomorrow(newEmptyRow.id);
    setEditedSlots([newEmptyRow, ...editedSlots]);
    setAddSlotButtonDisabled(true);
  };

  return (
    <div style={{ width: '100%' }}>
      <Row justify="start" style={{ marginBottom: '10px' }} gutter={24}>
        <Col span={6}>
          <Form.Item valuePropName={'date'}>
            <ConfigProvider locale={trTR}>
              <DatePicker
                placeholder={t('placeholder.selectDate')}
                format={'DD/MM/YYYY'}
                style={{ width: '100%' }}
                allowClear={false}
                value={selectDate ? moment.utc(selectDate) : null}
                onChange={(date) => {
                  if (date) {
                    const startOfDay = moment
                      .utc(date)
                      .startOf('day')
                      .valueOf();
                    setSelectDate(startOfDay);
                  } else {
                    setSelectDate(null);
                  }
                }}
              />
            </ConfigProvider>
          </Form.Item>
        </Col>
        {storeInputVisible && (
          <Col span={6}>
            <Select
              style={{ width: '100%' }}
              placeholder={t('selectStore')}
              onChange={handleChangeStore}
              showSearch
              filterOption={true}
              optionFilterProp="children"
              value={selectedStore ?? null}
            >
              {stores.map((store, index) => (
                <Option key={index} value={store.id}>
                  {`${store.code} - ${store.name}`}
                </Option>
              ))}
            </Select>
          </Col>
        )}
        <Col span={6}>
          <Select
            style={{ width: '100%' }}
            placeholder={t('selectZone')}
            onChange={handleChangeZone}
            showSearch
            optionFilterProp="children"
            value={selectedZoneId}
            loading={isLoadingZones}
            filterOption={(input, option) =>
              (option?.children ?? '')
                .toLocaleLowerCase('tr-TR')
                .includes(input.toLocaleLowerCase('tr-TR'))
            }
          >
            {zones.map((zone, index) => (
              <Option key={index} value={zone.id}>
                {`${zone.code} - ${zone.name}`}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>
      <Row>
        <Col>
          <Tooltip title={t('import')}>
            {isCrudEnabled && (
              <Button
                type=""
                shape="round"
                icon={<UploadOutlined />}
                onClick={() => setIsImportModalOpen(true)}
                disabled={false}
                style={{ marginBottom: '10px', marginRight: '3px' }}
              />
            )}
          </Tooltip>
        </Col>
      </Row>
      <Row gutter={6} style={{ width: '100%' }}>
        <Col span={12}>
          <Card>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              <h3>{formattedTodayDate}</h3>
              <span>{`${t('occupancy')}: ${todayOccupancy}%`}</span>
              <Tooltip title={t('addSlot')}>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => handleAddRow(SLOT_MONITORING_DAYS.TODAY)}
                  disabled={addSlotButtonDisabled}
                />
              </Tooltip>
            </div>
            <Form
              form={formToday}
              component={false}
              scroll={{ x: 'min-content' }}
            >
              <div className="SlotMonitoring">
                <Table
                  bordered
                  loading={loading}
                  dataSource={slotsForToday}
                  columns={columnsToday}
                  rowClassName="editable-row"
                  rowKey={(record) => record.id}
                  pagination={false}
                  className="responsive-table"
                  style={{ minWidth: 'min-content' }}
                />
              </div>
            </Form>
          </Card>
        </Col>

        <Col span={12}>
          <Card>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              <h3>{formattedTomorrowDate}</h3>
              <span>{`${t('occupancy')}: ${tomorrowOccupancy}%`}</span>
              <Tooltip title={t('addSlot')}>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => handleAddRow(SLOT_MONITORING_DAYS.TOMORROW)}
                  disabled={addSlotButtonDisabled}
                />
              </Tooltip>
            </div>
            <Form
              form={formTomorrow}
              component={false}
              scroll={{ x: 'min-content' }}
            >
              <div className="SlotMonitoring">
                <Table
                  bordered
                  loading={loading}
                  dataSource={slotsForTomorrow}
                  columns={columnsTomorrow}
                  rowClassName="editable-row"
                  rowKey={(record) => record.id}
                  pagination={false}
                  className="responsive-table"
                  style={{ minWidth: 'min-content' }}
                />
              </div>
            </Form>
          </Card>
        </Col>
      </Row>
      <Row
        style={{ display: 'flex', justifyContent: 'flex-end', margin: '10px' }}
      >
        <Button
          style={{ marginRight: '10px' }}
          icon={<SaveOutlined />}
          type="primary"
          disabled={
            !selectedZoneId ||
            (editedSlots.length === 0 && deletedSlots.length === 0)
          }
          onClick={saveCurrentSlots}
        >
          {t('save')}
        </Button>
      </Row>
      <Suspense fallback={<></>}>
        {isImportModalOpen && (
          <ImportBulkCapacityUpdate
            slotCapacityLink={slotCapacityLink}
            onCloseHandler={() => setIsImportModalOpen(false)}
            onSuccessCallback={() => {
              setLoading(true);
              setTimeout(() => {
                fetchData();
              }, 2000);
            }}
          />
        )}
      </Suspense>
    </div>
  );
};
export default SlotMonitoring;
